import { useEffect, useState } from "react";
import { Bell, Megaphone } from "lucide-react";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const STORAGE_KEY = "fisk_read_ann";

function loadReadIds() {
  try { return new Set(JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]")); }
  catch { return new Set(); }
}

function saveReadIds(set) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(set))); }
  catch (_) {}
}

export default function NotificationsBell() {
  const [items, setItems] = useState([]);
  const [read, setRead] = useState(loadReadIds);
  const [open, setOpen] = useState(false);

  const fetchList = async () => {
    try {
      const { data } = await api.get("/announcements");
      setItems(data);
    } catch (_) {}
  };

  useEffect(() => {
    fetchList();
    // Refresca a cada 60s
    const t = setInterval(fetchList, 60_000);
    return () => clearInterval(t);
  }, []);

  const unreadCount = items.filter((a) => !read.has(a.id)).length;

  const markAllRead = () => {
    const next = new Set(read);
    items.forEach((a) => next.add(a.id));
    setRead(next);
    saveReadIds(next);
  };

  const targetLabel = (t) => t === "sacoma" ? "Unid. Sacomã" : t === "pauliceia" ? "Unid. Paulicéia" : "Todas as unidades";

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o && unreadCount > 0) markAllRead();
      }}
    >
      <PopoverTrigger asChild>
        <button
          data-testid="notifications-bell"
          className="relative inline-flex items-center justify-center w-9 h-9 rounded-sm hover:bg-neutral-900 transition-colors text-white"
          aria-label={`Avisos ${unreadCount > 0 ? `(${unreadCount} novo${unreadCount > 1 ? 's' : ''})` : ''}`}
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span
              data-testid="notifications-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-[#E30613] text-white text-[10px] font-bold border-2 border-black"
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[340px] p-0 rounded-sm"
        data-testid="notifications-popover"
      >
        <div className="px-4 py-3 border-b border-neutral-200 bg-neutral-50 flex items-center gap-2">
          <Megaphone className="w-4 h-4 text-[#E30613]" />
          <div className="text-xs uppercase tracking-[0.2em] text-neutral-700 font-semibold">Avisos</div>
        </div>
        <div className="max-h-[360px] overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-6 text-sm text-neutral-500 text-center" data-testid="notifications-empty">
              Nenhum aviso no momento.
            </div>
          ) : (
            <ul className="divide-y divide-neutral-100">
              {items.map((a) => (
                <li key={a.id} data-testid={`notification-item-${a.id}`} className="p-3">
                  <p className="text-sm text-neutral-900 whitespace-pre-wrap break-words">{a.text}</p>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mt-1.5 font-semibold">
                    {targetLabel(a.target)}
                    {a.created_at && (
                      <span className="text-neutral-400 ml-2 normal-case tracking-normal">
                        · {new Date(a.created_at).toLocaleDateString("pt-BR")}
                      </span>
                    )}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
